/**
 * AI-CHAT-SUMMARY — derive the preview caption shown for a chat saved to
 * Captures (see `XaesAnalyst.handleSaveToLocker`).
 *
 * The caption is what a teammate reads when browsing the locker, so it
 * should describe *what was talked about* — not a generic
 * "Chat export — … (N turns)". Two layers, by design:
 *
 *   - {@link deterministicChatCaption} — a synchronous base caption built
 *     from the user's own prompts. Used immediately at save time (so the
 *     card reads usefully the instant it lands) and kept as the durable
 *     fallback when no API key is configured (mock mode) or the summary
 *     pass fails.
 *   - {@link summariseChat} — a best-effort background pass that reuses the
 *     Analyst chat backend selector (`streamAgentReply` when
 *     `VITE_CHAT_BACKEND=agent`, else `streamXaesReply`) to replace the base
 *     caption with a one-line topic summary once it lands. Returns `null`
 *     when neither transport is available / on failure so the caller keeps
 *     the deterministic caption.
 *
 * The caller commits the upgrade via the IDB-only `useCaptures.update`, so
 * the caption edit never round-trips the blob-sync path (it's metadata).
 *
 * Source references:
 *   - `ai-draft.ts` — shared AI-draft plumbing; `buildSummaryPrompt`
 *     mirrors this prompt shape for the workshop-handoff surface.
 *   - `use-insight-suggestions.ts` — same transcript-render convention.
 */

import { isAgentChatBackend, isClaudeConfigured } from "@/env";
import { buildSystemPrompt, type ChatScope } from "./context-builder";
import type { ChatMessage } from "./use-chat";
import type { ChatSession } from "./persistence";
import { streamAgentReply } from "./agent-api";
import { streamXaesReply } from "./vercel-ai";

/** Max length of the preview caption (both the deterministic and AI
 *  variants) — keeps the lightbox caption to a tidy single line. */
const CAPTION_MAX_LEN = 200;
/** Transcript budget handed to the summariser. Clipped from the head so
 *  the opening turns — which establish the topic — always survive. */
const TRANSCRIPT_MAX_LEN = 6000;
/** Hard ceiling on the background summary request so a hung stream can't
 *  run forever. */
const SUMMARY_TIMEOUT_MS = 20_000;

/** Clip `text` to `max` chars, appending an ellipsis when it overflows. */
function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * Render completed conversation turns as a plain transcript. Streaming /
 * errored / empty bubbles are skipped — they aren't real turns. Mirrors
 * the `buildTranscript` helper in `use-insight-suggestions.ts`.
 */
export function messagesToTranscript(messages: ChatMessage[]): string {
  const lines: string[] = [];
  for (const m of messages) {
    if (m.streaming || m.error) continue;
    const text = m.text.trim();
    if (!text) continue;
    lines.push(`${m.role === "user" ? "User" : "XAES Analyst"}: ${text}`);
  }
  return lines.join("\n\n");
}

/**
 * Synchronous fallback caption — the user's own questions joined, which
 * read as "what was talked about" without any model call. Used as the
 * immediate caption and retained whenever the AI pass is unavailable.
 */
export function deterministicChatCaption(session: ChatSession): string {
  const prompts = session.messages
    .filter((m) => m.role === "user" && !m.streaming && !m.error)
    .map((m) => m.text.trim().replace(/\s+/g, " "))
    .filter((t) => t.length > 0);
  if (prompts.length === 0) return "Conversation with the XAES Analyst.";
  return clip(prompts.join(" · "), CAPTION_MAX_LEN);
}

/**
 * Assemble the one-line-summary prompt. Grounded on the same system
 * prompt the Analyst uses; mirrors `buildSummaryPrompt` in `ai-draft.ts`.
 */
export function buildChatSummaryPrompt(
  scope: ChatScope,
  transcript: string,
): { system: string; prompt: string } {
  const system = buildSystemPrompt({ scope, resolvedReferences: [] });
  const clipped = clip(transcript, TRANSCRIPT_MAX_LEN);
  const prompt = [
    "Summarise what this conversation was about in ONE sentence (≤ 30 words),",
    "so a teammate browsing saved chat captures knows what was discussed at a glance.",
    "Describe the topics and questions covered. Ground it strictly in the conversation.",
    "Output ONLY the summary sentence — no heading, no preamble, no surrounding quotes.",
    "",
    "<conversation>",
    clipped,
    "</conversation>",
  ].join("\n");
  return { system, prompt };
}

/**
 * Clean a model draft into a one-line caption. Rejects the keyless mock
 * banner (returns `""`), strips fences / a leading label / surrounding
 * quotes, collapses whitespace, and clips. Returns `""` when nothing
 * usable remains so the caller keeps the deterministic caption.
 */
export function cleanSummary(draft: string): string {
  if (!draft) return "";
  // Keyless mock banner — never a real summary.
  if (/🤖|_Mock mode_/i.test(draft)) return "";
  let s = draft.replace(/```(?:json|markdown)?/gi, "").trim();
  // Drop a leading label the model occasionally prepends.
  s = s.replace(/^\s*(?:summary|topic|discussion)\s*[:\-—]\s*/i, "");
  // Strip surrounding quotes (straight + curly).
  s = s.replace(/^["'“”]+|["'“”]+$/g, "").trim();
  // Collapse to a single line.
  s = s.replace(/\s+/g, " ").trim();
  return s ? clip(s, CAPTION_MAX_LEN) : "";
}

/**
 * Best-effort background summary of a saved chat. Returns the cleaned
 * one-line caption, or `null` when summarisation isn't possible (mock
 * mode, empty transcript, request error / timeout) — in which case the
 * caller keeps the deterministic caption. Never throws.
 */
export async function summariseChat({
  session,
  scope,
  model,
}: {
  session: ChatSession;
  scope: ChatScope;
  model: string;
}): Promise<string | null> {
  if (!isAgentChatBackend() && !isClaudeConfigured()) return null;
  const transcript = messagesToTranscript(session.messages);
  if (!transcript.trim()) return null;

  const { system, prompt } = buildChatSummaryPrompt(scope, transcript);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SUMMARY_TIMEOUT_MS);
  try {
    const messages = [{ role: "user" as const, content: prompt }];
    const stream = isAgentChatBackend()
      ? streamAgentReply({ system, messages, signal: controller.signal })
      : streamXaesReply({
          system,
          messages,
          signal: controller.signal,
          model,
        });
    let acc = "";
    for await (const delta of stream) {
      acc += delta;
    }
    return cleanSummary(acc) || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
